import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Clock, Star, Phone, Bug, Search, FileText, Hammer, CalendarCheck, Crown, MapPin } from "lucide-react";
import { useSeo, faqLd } from "@/lib/seo";
import { CtaBand, Faq, SisterLinks, VisualNote } from "@/components/public/Blocks";
import { PHONE, PHONE_HREF, FRANCHISE_DEPTS, REVIEWS } from "@/data/site";
import { BlogTeaser } from "@/pages/public/Blog";

const PESTS = [
  { to: "/traitement-termites", name: "Termites", text: "Injection, pulvérisation, pièges-appâts.", img: "/images/poutre.webp" },
  { to: "/traitement-capricorne", name: "Capricorne", text: "Charpentes résineuses attaquées.", img: "/images/capricorne.webp" },
  { to: "/traitement-vrillette", name: "Vrillette", text: "Planchers, poutres et meubles.", img: "/images/vrillette.webp" },
  { to: "/traitement-moustiques", name: "Moustiques", text: "Moustique tigre autour de chez vous.", img: "/images/moustique.webp" },
  { to: "/traitement-humidite", name: "Humidité", text: "Remontées capillaires, salpêtre.", img: "/images/humidite.webp" },
];

const SIGNS = ["Cordonnets de terre sur un mur", "Bois qui sonne creux", "Insectes ailés au printemps", "Petits trous dans le bois", "Sciure / poudre au sol", "Plinthes ou portes déformées", "Murs humides, salpêtre", "Maison en zone termites (Gironde…)"];

const STEPS = [
  [Search, "Inspection visuelle*", "Repérage des indices, photos, explication claire."],
  [FileText, "Devis sous 48 h", "Chiffrage précis, méthode adaptée, aucune surprise."],
  [Hammer, "Traitement pro", "Produits professionnels, techniciens Certibiocide."],
  [CalendarCheck, "Suivi & garantie", "Attestation de traitement, garantie 5 ans*."],
];

const HOME_FAQ = [
  { q: "Intervenez-vous rapidement ?", a: "Oui, nous intervenons rapidement sur toute la Gironde et remettons un devis sous 48 h." },
  { q: "Votre inspection vaut-elle diagnostic termites ?", a: "Non : notre inspection est uniquement visuelle*. Le diagnostic réglementaire pour une vente doit être réalisé par un diagnostiqueur immobilier certifié." },
  { q: "Quelle garantie sur vos traitements ?", a: "Une garantie commerciale de 5 ans, hors pose de pièges." },
  { q: "Recherchez-vous des franchisés ?", a: "Oui, dans les départements 24, 47, 40, 17, 16, 64 et 31. Rendez-vous sur la page Franchise." },
];

function Hero() {
  const stats = [[ShieldCheck, "5 ans", "garantie commerciale"], [Clock, "48 h", "pour votre devis"], [Star, "5,0/5", "avis Google"], [Crown, "+5 ans", "d'existence"]];
  return (
    <section className="grain relative overflow-hidden bg-[#1E2422] text-white">
      <div className="hex-bg absolute inset-0 opacity-60" />
      <div className="container-x relative grid items-center gap-10 py-14 lg:grid-cols-12 lg:py-24">
        <div className="lg:col-span-7">
          <p className="fade-up inline-flex items-center gap-2 rounded-full border border-[#E9A46A]/40 bg-[#C06014]/20 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#F2C29B]">
            <Bug className="h-3.5 w-3.5" /> Termites · Xylophages · Moustiques · Humidité
          </p>
          <h1 className="fade-up mt-5 text-4xl font-black leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl" data-testid="home-h1">
            Les termites travaillent en silence.<span className="block text-[#E9A46A]">Nous, on agit.</span>
          </h1>
          <p className="fade-up mt-6 max-w-xl text-base text-white/80 md:text-lg" style={{ animationDelay: ".1s" }}>
            TERMITE HABITAT traite termites, capricornes, vrillettes, moustiques et humidité. Inspection visuelle*, devis sous 48 h et garantie commerciale 5 ans.
          </p>
          <div className="fade-up mt-8 flex flex-wrap gap-3" style={{ animationDelay: ".2s" }}>
            <Link to="/contact" className="btn-lift flex items-center gap-2 rounded-full bg-[#C06014] px-7 py-3.5 font-bold" data-testid="hero-quote-button">Demander un devis <ArrowRight className="h-4 w-4" /></Link>
            <a href={PHONE_HREF} className="btn-lift flex items-center gap-2 rounded-full bg-white px-7 py-3.5 font-bold text-[#1E2422]" data-testid="hero-call-button"><Phone className="h-4 w-4" /> {PHONE}</a>
          </div>
        </div>
        <div className="relative lg:col-span-5">
          <img src="/images/charpente.webp" alt="Charpente bois protégée contre les termites" className="aspect-[4/5] w-full rounded-2xl object-cover shadow-2xl" fetchPriority="high" />
          <div className="absolute -bottom-6 -left-4 right-6 grid grid-cols-2 gap-2 rounded-2xl border border-white/20 bg-white/10 p-3 backdrop-blur-xl sm:-left-8">
            {stats.map(([Icon, v, l], i) => (
              <motion.div key={l} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.1 }} className="rounded-xl bg-[#1E2422]/70 p-3" data-testid={`hero-stat-${i}`}>
                <Icon className="h-4 w-4 text-[#E9A46A]" />
                <p className="mt-1 font-display text-2xl font-black">{v}</p>
                <p className="text-xs text-white/70">{l}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      <div className="relative overflow-hidden border-t border-white/10 py-3 text-sm font-semibold text-white/60">
        <div className="marquee flex w-max gap-10 whitespace-nowrap">
          {[0, 1].map((k) => ["Traitement termites Gironde", "Traitement charpente", "Capricorne des maisons", "Vrillette", "Pièges-appâts", "Injection anti-termites", "Moustique tigre", "Remontées capillaires", "Garantie 5 ans", "Franchise TERMITE HABITAT"].map((w) => <span key={`${k}${w}`}>✦ {w}</span>))}
        </div>
      </div>
    </section>
  );
}

function Pests() {
  return (
    <section className="container-x py-20">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#53634F]">Nos traitements</p>
          <h2 className="mt-2 max-w-xl text-3xl font-black tracking-tight md:text-4xl">Un nuisible ? Une solution professionnelle.</h2>
        </div>
        <Link to="/services" className="flex items-center gap-1 font-semibold text-[#C06014]" data-testid="home-services-link">Tous nos services <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        {PESTS.map((p, i) => (
          <Link key={p.to} to={p.to} className={`card-hover group relative overflow-hidden rounded-2xl border bg-[#1E2422] ${i < 2 ? "lg:col-span-3 h-80" : "lg:col-span-2 h-64"}`} data-testid={`home-pest-${p.name.toLowerCase()}`}>
            <img src={p.img} alt={`Traitement ${p.name}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1E2422] via-[#1E2422]/30 to-transparent" />
            <div className="absolute bottom-0 p-6 text-white">
              <h3 className="text-2xl font-black">{p.name}</h3>
              <p className="text-sm text-white/80">{p.text}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-[#E9A46A]">Découvrir <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function RiskTest() {
  const [checked, setChecked] = useState([]);
  const toggle = (s) => setChecked(checked.includes(s) ? checked.filter((x) => x !== s) : [...checked, s]);
  const score = Math.round((checked.length / SIGNS.length) * 100);
  const level = checked.length === 0 ? ["Cochez les signes observés", "#53634F"] : checked.length <= 2 ? ["Vigilance : faites vérifier", "#B98B1E"] : checked.length <= 4 ? ["Risque élevé : agissez vite", "#C06014"] : ["Alerte : intervention conseillée", "#A3341A"];
  return (
    <section className="bg-white py-20">
      <div className="container-x grid gap-10 lg:grid-cols-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#53634F]">Test express</p>
          <h2 className="mt-2 text-3xl font-black tracking-tight md:text-4xl">Votre maison est-elle à risque ?</h2>
          <p className="mt-3 text-muted-foreground">Cochez ce que vous avez remarqué. Ce test indicatif ne remplace pas une inspection visuelle* par un professionnel.</p>
          <div className="mt-8 rounded-2xl bg-[#F5F3E9] p-6" data-testid="risk-result">
            <p className="font-display text-xl font-black" style={{ color: level[1] }}>{level[0]}</p>
            <div className="mt-3 h-3 overflow-hidden rounded-full bg-black/10">
              <motion.div className="h-full rounded-full" style={{ background: level[1] }} animate={{ width: `${score}%` }} />
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{checked.length} signe(s) sur {SIGNS.length}</p>
            {checked.length > 0 && <Link to="/contact" className="btn-lift mt-5 inline-flex items-center gap-2 rounded-full bg-[#C06014] px-6 py-3 font-bold text-white" data-testid="risk-cta-button">Faire vérifier ma maison <ArrowRight className="h-4 w-4" /></Link>}
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {SIGNS.map((s, i) => {
            const on = checked.includes(s);
            return (
              <button key={s} onClick={() => toggle(s)} className={`card-hover rounded-xl border p-4 text-left font-semibold ${on ? "border-[#C06014] bg-[#C06014] text-white" : "bg-[#F5F3E9]/60"}`} data-testid={`risk-sign-${i}`} aria-pressed={on}>
                {s}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Steps() {
  return (
    <section className="container-x py-20">
      <p className="text-xs font-bold uppercase tracking-widest text-[#53634F]">Notre méthode</p>
      <h2 className="mt-2 text-3xl font-black tracking-tight md:text-4xl">4 étapes pour un habitat protégé</h2>
      <div className="mt-10 grid gap-4 md:grid-cols-4">
        {STEPS.map(([Icon, t, d], i) => (
          <motion.div key={t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="card-hover relative rounded-2xl border bg-white p-6">
            <span className="absolute right-5 top-4 font-display text-5xl font-black text-black/5">0{i + 1}</span>
            <Icon className="h-8 w-8 text-[#C06014]" />
            <h3 className="mt-4 text-lg font-black">{t}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{d}</p>
          </motion.div>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap gap-4 text-sm font-semibold">
        <Link to="/methodes" className="text-[#C06014] underline-offset-4 hover:underline">Nos méthodes</Link>
        <Link to="/traitements-preventif-curatif" className="text-[#C06014] underline-offset-4 hover:underline">Préventif ou curatif ?</Link>
        <Link to="/certibiocide" className="text-[#C06014] underline-offset-4 hover:underline">Certibiocide</Link>
      </div>
    </section>
  );
}

function Gallery() {
  const pics = [["/images/cordonnets.webp", "Poutre ravagée, traces de cordonnets"], ["/images/piege.webp", "Piège-appât posé"], ["/images/volantes.webp", "Termites ailés (essaimage)"], ["/images/brule.webp", "Bois contaminés brûlés sur place"]];
  return (
    <section className="bg-[#1E2422] py-20 text-white">
      <div className="container-x">
        <p className="text-xs font-bold uppercase tracking-widest text-[#E9A46A]">Sur le terrain</p>
        <h2 className="mt-2 text-3xl font-black tracking-tight md:text-4xl">Nos chantiers en Gironde</h2>
        <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4">
          {pics.map(([src, alt]) => (
            <figure key={src} className="group overflow-hidden rounded-xl">
              <img src={src} alt={alt} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <figcaption className="mt-2 text-xs text-white/60">{alt}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function FranchiseTeaser() {
  return (
    <section className="container-x py-20" data-testid="home-franchise-section">
      <div className="grain relative overflow-hidden rounded-3xl bg-[#53634F] p-8 text-white md:p-14">
        <div className="hex-bg absolute inset-0 opacity-50" />
        <div className="relative grid gap-10 lg:grid-cols-2">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-widest"><Crown className="h-3.5 w-3.5" /> Devenir franchisé</p>
            <h2 className="mt-4 text-3xl font-black tracking-tight md:text-5xl">Rejoins le réseau TERMITE HABITAT</h2>
            <p className="mt-4 max-w-lg text-white/85">Deviens ton propre patron, en exclusivité sur ton département, avec une marque, des méthodes et tous les outils pour démarrer. Nous recherchons nos futurs franchisés :</p>
            <Link to="/franchise" className="btn-lift mt-8 inline-flex items-center gap-2 rounded-full bg-[#C06014] px-7 py-3.5 font-bold" data-testid="home-franchise-cta">Découvrir la franchise <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {FRANCHISE_DEPTS.map((d) => (
              <Link key={d.code} to={`/franchise#dep-${d.code}`} className="card-hover rounded-xl border border-white/20 bg-white/10 p-4 backdrop-blur" data-testid={`home-dept-${d.code}`}>
                <p className="font-display text-3xl font-black">{d.code}</p>
                <p className="text-sm text-white/80">{d.name}</p>
                <p className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#F2C29B]"><MapPin className="h-3 w-3" /> Disponible</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  return (
    <section className="container-x py-10">
      <div className="grid gap-4 md:grid-cols-3">
        {REVIEWS.map((r) => (
          <blockquote key={r.text} className="rounded-2xl border bg-white p-6">
            <div className="flex gap-0.5 text-[#C06014]">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className="h-4 w-4 fill-current" />)}</div>
            <p className="mt-3 text-sm leading-relaxed">« {r.text} »</p>
            <footer className="mt-3 text-xs font-bold text-muted-foreground">{r.who}</footer>
          </blockquote>
        ))}
      </div>
      <Link to="/garantie-5-ans" className="mt-6 flex items-center justify-between rounded-2xl bg-[#C06014]/10 p-6 font-display text-lg font-black text-[#C06014]" data-testid="home-guarantee-link">
        <span className="flex items-center gap-3"><ShieldCheck className="h-6 w-6" /> Garantie commerciale 5 ans (hors pose de pièges)</span><ArrowRight />
      </Link>
    </section>
  );
}

export default function Home() {
  useSeo({
    title: "TERMITE HABITAT – Traitement termites, capricorne, vrillette, humidité | Devis 48 h",
    description: "Traitement des termites, capricornes, vrillettes, moustiques et humidité en Gironde et Nouvelle-Aquitaine. Inspection visuelle*, devis 48 h, garantie 5 ans. Devenez franchisé.",
    path: "/",
    image: "/images/charpente.webp",
    jsonLd: { "@context": "https://schema.org", "@graph": [{ "@type": "WebSite", name: "TERMITE HABITAT", url: "https://www.termitehabitat.fr/", inLanguage: "fr-FR" }, faqLd(HOME_FAQ)] },
  });
  return (
    <>
      <Hero />
      <Pests />
      <RiskTest />
      <Steps />
      <Gallery />
      <FranchiseTeaser />
      <Reviews />
      <BlogTeaser />
      <Faq faq={HOME_FAQ} />
      <CtaBand />
      <SisterLinks />
      <div className="container-x pb-10"><VisualNote /></div>
    </>
  );
}
